import { useEffect, useRef, useState } from "react";
import { RootState, useAppSelector } from "../../../../../state/state.tsx";
import { SubscriptionDocWithId } from "../../../types/SubscriptionType.ts";
import SubscriptionRowsRenderer from "./SubscriptionRowsRenderer.tsx";
import { Tooltip } from "@mui/material";
import { selectActiveRole } from "../../../../../state/UserStatesV2/userSliceV2.ts";
import { openModal } from "../../../../../state/ModalSlice/ModalSlice.ts";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../../../state/state.tsx";
import { mobileStyles, cn } from "../../../constants/mobileStyles";

type filter = {
    Active: boolean,
    Hidden: boolean,
    locations: string[],
}

export default function Subscriptions() {

    // Initialize filters from localStorage or default values
    const [filters, setFilters] = useState<filter>(() => {
        const savedFilters = localStorage.getItem('subscriptionFilters');
        if (savedFilters) {
            try {
                return JSON.parse(savedFilters);
            } catch (error) {
                console.error('Error parsing saved filters:', error);
            }
        }
        return {
            Active: true,
            Hidden: false,
            locations: [],
        };
    });

    // Save filters to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('subscriptionFilters', JSON.stringify(filters));
    }, [filters]);

    const Dispatch = useAppDispatch()
    // Initialize viewType from localStorage or default to 'grid'
    const [viewType, setViewType] = useState<'list' | 'grid'>(() => {
        const savedViewType = localStorage.getItem('subscriptionViewType');
        return (savedViewType === 'list' || savedViewType === 'grid') ? savedViewType : 'grid';
    });

    // Update localStorage when viewType changes
    const handleViewTypeChange = (type: 'list' | 'grid') => {
        setViewType(type);
        localStorage.setItem('subscriptionViewType', type);
    };

    // State to manage dropdown visibility
    const [isDropdownVisible, setDropdownVisible] = useState(false);
    const [isLocationDropdownVisible, setLocationDropdownVisible] = useState(false);

    // Ref to track the dropdown element
    const dropdownRef: any = useRef(null);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: { target: any; }) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownVisible(false);
                setLocationDropdownVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Handler for changing filter state
    const handleFilterChange = (filter: string) => {
        setFilters((prevFilters: any) => ({
            ...prevFilters,
            [filter]: !prevFilters[filter],
        }));
    };

    // Handler for location selection
    const handleLocationToggle = (location: string) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            locations: prevFilters.locations.includes(location)
                ? prevFilters.locations.filter(loc => loc !== location)
                : [...prevFilters.locations, location]
        }));
    };

    // Clear all filters
    const clearAllFilters = () => {
        setFilters({
            Active: true,
            Hidden: false,
            locations: [],
        });
    };

    // Toggle dropdown visibility
    const toggleDropdown = () => {
        setDropdownVisible(!isDropdownVisible);
    };

    // Toggle location dropdown visibility
    const toggleLocationDropdown = () => {
        setLocationDropdownVisible(!isLocationDropdownVisible);
    };

    // Count the number of selected filters
    const selectedCount = Object.values(filters).filter(value => {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return Boolean(value);
    }).length;

    // Get active filter summary
    const getActiveFilterSummary = () => {
        const activeParts = [];
        
        if (filters.Active && filters.Hidden) {
            activeParts.push('All Status');
        } else if (filters.Active) {
            activeParts.push('Active Batches');
        } else if (filters.Hidden) {
            activeParts.push('Inactive Batches');
        }
        
        if (filters.locations.length > 0) {
            if (filters.locations.length === 1) {
                activeParts.push(`📍 ${filters.locations[0]}`);
            } else {
                activeParts.push(`📍 ${filters.locations.length} locations`);
            }
        }
        
        return activeParts.join(' • ');
    };

    // State for search query
    const [searchQuery, setSearchQuery] = useState("");

    // Add event handler for ESC key
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setSearchQuery('');
        }
    };

    // -----Accessing Subscription List State and Loading State
    const SubscriptionList: SubscriptionDocWithId[] = useAppSelector((state: RootState) => state.subscriptionTableSlice.data) || [];

    // Get unique place names from subscription data
    const availableLocations = [...new Set(SubscriptionList
        .map(item => item.placeName)
        .filter(placeName => placeName && placeName.trim() !== '')
    )].sort();

    // Filter subscriptions based on search query and filters
    const filteredSubscriptions = SubscriptionList.filter((item) => {
        const matchesSearch = item.Name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter = (item.showMeInList && filters.Active) || (!item.showMeInList && filters.Hidden);
        
        // Location filtering logic
        const matchesLocation = filters.locations.length === 0 || filters.locations.includes(item.placeName);
        
        return matchesSearch && matchesFilter && matchesLocation;
    });

    const activeRole = useAppSelector(selectActiveRole)

    const role = activeRole?.role

    function onClickAddSub() {
        if (role === 'Observer' || role === "Manager") {
            toast.error("You are not allowed to add subscriptions")
            return
        }
        Dispatch(openModal("AddSubOpen"))
    }

    return (
        <div className={cn(mobileStyles.padding.container, "mt-4")}>
            <div className={cn(mobileStyles.flex.between, mobileStyles.margin.bottom.medium, "flex-wrap gap-3")}>
                <div className="flex items-center">
                    <p className={mobileStyles.text.h3}>My Batches</p>
                </div>
                <div className={cn("flex items-center", mobileStyles.gap.small, "flex-wrap w-full sm:w-auto")}>
                    {/* Add Button */}
                    <button
                        className={cn(mobileStyles.button.primary, "bg-cc_brand text-white hover:bg-cc_brand_hover flex items-center gap-1.5 sm:gap-2")}
                        onClick={onClickAddSub}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className={mobileStyles.icon.small} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        <span className="hidden sm:inline">Add Batch</span>
                        <span className="sm:hidden">Add</span>
                    </button>
                    
                    {/* Search Input */}
                    <div className="relative flex-1 sm:flex-initial min-w-[150px] sm:min-w-[200px]">
                        <input
                            type="text"
                            placeholder="Search batches..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className={cn(mobileStyles.input.search, "pr-8 sm:pr-10")}
                        />
                        <div className="absolute right-2.5 sm:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                    
                    {/* View Toggle Buttons - Hide list view on mobile */}
                    <div className="hidden sm:flex items-center gap-1 sm:gap-2">
                        <Tooltip title="List View">
                            <button
                                onClick={() => handleViewTypeChange('list')}
                                className={cn(mobileStyles.button.iconSmall, viewType === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </Tooltip>
                        <Tooltip title="Grid View">
                            <button
                                onClick={() => handleViewTypeChange('grid')}
                                className={cn(mobileStyles.button.iconSmall, viewType === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600')}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                            </button>
                        </Tooltip>
                    </div>
                    
                    {/* Filter Component */}
                    <div className="relative inline-block" ref={dropdownRef}>
                        <button
                            type="button"
                            className={cn(mobileStyles.button.small, "inline-flex items-center gap-x-1.5 sm:gap-x-2 border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50")}
                            aria-haspopup="true"
                            aria-expanded={isDropdownVisible}
                            onClick={toggleDropdown}
                        >
                            <svg
                                className="flex-shrink-0 w-3.5 h-3.5 sm:w-4 sm:h-4"
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M3 6h18" />
                                <path d="M7 12h10" />
                                <path d="M10 18h4" />
                            </svg>
                            <span className="hidden sm:inline">Filter</span>
                            <span
                                className="ps-1 sm:ps-2 w-4 text-xs font-semibold text-blue-600 border-s border-gray-200">
                                {selectedCount}
                            </span>
                        </button>
                        {isDropdownVisible && (
                            <div
                                className="absolute right-0 sm:left-0 transition-[opacity,margin] duration opacity-100 block divide-y divide-gray-200 min-w-[200px] sm:min-w-48 z-10 bg-white shadow-md rounded-lg mt-2 max-h-[80vh] overflow-y-auto"
                                aria-labelledby="hs-as-table-table-filter-dropdown"
                            >
                                <div className="divide-y divide-gray-200 ">
                                    <label htmlFor="filter-published" className="flex py-2.5 px-3">
                                        <input
                                            type="checkbox"
                                            className="shrink-0 mt-0.5 border-gray-300 rounded text-blue-600 focus:ring-blue-500 "
                                            id="filter-published"
                                            checked={filters.Active}
                                            onChange={() => handleFilterChange('Active')}
                                        />
                                        <span className="ms-3 text-sm text-gray-800">Active</span>
                                    </label>
                                    <label htmlFor="filter-pending" className="flex py-2.5 px-3">
                                        <input
                                            type="checkbox"
                                            className="shrink-0 mt-0.5 border-gray-300 rounded text-blue-600 focus:ring-blue-500 "
                                            id="filter-pending"
                                            checked={filters.Hidden}
                                            onChange={() => handleFilterChange('Hidden')}
                                        />
                                        <span className="ms-3 text-sm text-gray-800 ">Inactive</span>
                                    </label>
                                </div>
                                
                                {/* Location Filter Section */}
                                {availableLocations.length > 0 && (
                                    <div className="py-2">
                                        <div className="px-3 py-2">
                                            <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <span>Location</span>
                                            </div>
                                            <div className="mt-2 relative">
                                                <button
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left text-sm bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    onClick={toggleLocationDropdown}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-gray-700">
                                                            {filters.locations.length === 0 
                                                                ? 'Select locations...' 
                                                                : `${filters.locations.length} selected`}
                                                        </span>
                                                        <svg 
                                                            className={`w-4 h-4 transition-transform ${isLocationDropdownVisible ? 'rotate-180' : ''}`}
                                                            fill="none" 
                                                            stroke="currentColor" 
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </div>
                                                </button>
                                                
                                                {isLocationDropdownVisible && (
                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto z-20">
                                                        {availableLocations.map((location) => (
                                                            <label key={location} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    className="shrink-0 border-gray-300 rounded text-blue-600 focus:ring-blue-500"
                                                                    checked={filters.locations.includes(location)}
                                                                    onChange={() => handleLocationToggle(location)}
                                                                />
                                                                <span className="ms-3 text-sm text-gray-800">{location}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Show selected locations */}
                                            {filters.locations.length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {filters.locations.map((location) => (
                                                        <span key={location} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                            {location}
                                                            <button
                                                                type="button"
                                                                onClick={() => handleLocationToggle(location)}
                                                                className="hover:bg-blue-200 rounded-full p-0.5"
                                                            >
                                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Clear filters button */}
                                {selectedCount > 0 && (
                                    <div className="py-2 px-3 border-t border-gray-200">
                                        <button
                                            type="button"
                                            onClick={clearAllFilters}
                                            className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-md transition-colors"
                                        >
                                            Clear all filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Active Filters Summary */}
            {selectedCount > 0 && (
                <div className="mb-4 flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            <span className="text-sm font-medium text-yellow-900">Active Filters:</span>
                        </div>
                        <span className="text-sm text-yellow-800">{getActiveFilterSummary()}</span>
                    </div>
                    <button
                        onClick={clearAllFilters}
                        className="flex items-center gap-1 text-sm text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 px-2 py-1 rounded-md transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Clear all
                    </button>
                </div>
            )}

            {/* {activeRole?.role !== "Observer" && <AddSubscriptionDashedComponent />}  */}

            {/* No results message */}
            {filteredSubscriptions.length === 0 && searchQuery && (
                <div className="text-center py-8">
                    <p className="text-gray-500">No subscriptions found matching "{searchQuery}"</p>
                </div>
            )}

            {/* Subscription rows */}
            <div className={`pt-2 ${viewType === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' : ''}`}>
                {filteredSubscriptions.map((item: SubscriptionDocWithId) => (
                    <SubscriptionRowsRenderer 
                        key={item.id}
                        {...item}
                        viewType={viewType}
                    />
                ))}
            </div>
        </div>
    )
}